import { createContext, ReactNode, useContext, useState } from "react"
import { useLazyQuery } from "@apollo/client"
import { useTranslation } from "react-i18next"
import { MainContext } from "./main"
import { AuthContext } from "./auth"
import { logger, Status } from "../services/utils/utils"
import {
  dashboardCommuteEmissionsMonthlyList,
  dashboardCommuteModesMonthlyList,
  dashboardMobilityCountriesList,
  dashboardMobilitySustainableSummaryGet,
  dashboardMobilityTransportList,
  dashboardMobilityWeeklyList,
} from "../services/graphql/queries"
import {
  CommuteEmissionsMonthItem,
  CommuteModeMonthItem,
  MobilityCountryItem,
  MobilitySustainableSummary,
  MobilityTransportItem,
  MobilityWeekItem,
} from "../models/mobility.models"

interface MobilityContextInterface {
  mobilityAssistantDataLoading: boolean
  mobilityTransportList: MobilityTransportItem[]
  mobilityWeeklyList: MobilityWeekItem[]
  mobilityCountriesList: MobilityCountryItem[]
  mobilitySustainableSummary: MobilitySustainableSummary | null
  getMobilityAssistantData: () => Promise<void>
  commuteEmissionsDataLoading: boolean
  commuteEmissionsMonthlyList: CommuteEmissionsMonthItem[]
  commuteModesMonthlyList: CommuteModeMonthItem[]
  getCommuteEmissionsData: () => Promise<void>
}

const MobilityContext = createContext<MobilityContextInterface>({
  mobilityAssistantDataLoading: true,
  mobilityTransportList: [],
  mobilityWeeklyList: [],
  mobilityCountriesList: [],
  mobilitySustainableSummary: null,
  getMobilityAssistantData: async () => {},
  commuteEmissionsDataLoading: true,
  commuteEmissionsMonthlyList: [],
  commuteModesMonthlyList: [],
  getCommuteEmissionsData: async () => {},
})

const MobilityController = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const { team } = useContext(AuthContext)
  const { getInput, setError, setErrorMessage } = useContext(MainContext)

  // states
  const [mobilityAssistantDataLoading, setMobilityAssistantDataLoading] =
    useState<boolean>(false)
  const [mobilityTransportList, setMobilityTransportList] = useState<
    MobilityTransportItem[]
  >([])
  const [mobilityWeeklyList, setMobilityWeeklyList] = useState<
    MobilityWeekItem[]
  >([])
  const [mobilityCountriesList, setMobilityCountriesList] = useState<
    MobilityCountryItem[]
  >([])
  const [mobilitySustainableSummary, setMobilitySustainableSummary] =
    useState<MobilitySustainableSummary | null>(null)
  const [commuteEmissionsDataLoading, setCommuteEmissionsDataLoading] =
    useState<boolean>(false)
  const [commuteEmissionsMonthlyList, setCommuteEmissionsMonthlyList] =
    useState<CommuteEmissionsMonthItem[]>([])
  const [commuteModesMonthlyList, setCommuteModesMonthlyList] = useState<
    CommuteModeMonthItem[]
  >([])

  // queries
  const [dashboardMobilityTransportListQuery] = useLazyQuery(
    dashboardMobilityTransportList
  )
  const [dashboardMobilityWeeklyListQuery] = useLazyQuery(
    dashboardMobilityWeeklyList
  )
  const [dashboardMobilityCountriesListQuery] = useLazyQuery(
    dashboardMobilityCountriesList
  )
  const [dashboardMobilitySustainableSummaryGetQuery] = useLazyQuery(
    dashboardMobilitySustainableSummaryGet
  )
  const [dashboardCommuteEmissionsMonthlyListQuery] = useLazyQuery(
    dashboardCommuteEmissionsMonthlyList
  )
  const [dashboardCommuteModesMonthlyListQuery] = useLazyQuery(
    dashboardCommuteModesMonthlyList
  )

  // period input from the global date picker (a missing start or end means
  // the whole history or up to now, as for the other dashboard queries)
  const getPeriodInput = () => {
    const input = getInput(team!.id)

    return {
      teamId: input.teamId,
      startDatetime: input.startDatetime,
      endDatetime: input.endDatetime,
    }
  }

  // fetch all pages of a list query
  const fetchAllPages = async <T,>(
    query: (options: any) => Promise<any>,
    field: string,
    input: object
  ) => {
    const list: T[] = []
    let nextToken: string | null = null

    do {
      const result: any = await query({
        variables: { input: { ...input, limit: 1000, nextToken } },
      })
      if (result.error) {
        throw result.error
      }

      list.push(...(result.data[field]?.items ?? []).filter(Boolean))
      nextToken = result.data[field]?.nextToken ?? null
    } while (nextToken)

    return list
  }

  // get a mobility assistant list
  const getMobilityList = async <T,>(
    query: (options: any) => Promise<any>,
    field: string,
    setter: (list: T[]) => void,
    errorLabel: string
  ) => {
    try {
      logger(Status.Api, `[MOBILITY] QUERY ${field}`)

      const list = await fetchAllPages<T>(query, field, getPeriodInput())
      logger(Status.Info, `[MOBILITY] ${field}`, list)

      setter(list)

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} ${errorLabel}`)
      setter([])

      return false
    }
  }

  // get mobility sustainable summary
  const getMobilitySustainableSummary = async () => {
    try {
      logger(Status.Api, "[MOBILITY] QUERY mobilitySustainableSummaryGet")

      const result: any = await dashboardMobilitySustainableSummaryGetQuery({
        variables: { input: getPeriodInput() },
      })
      if (result.error) {
        throw result.error
      }
      logger(
        Status.Info,
        "[MOBILITY] mobility sustainable summary",
        result.data.dashboardMobilitySustainableSummaryGet
      )

      setMobilitySustainableSummary(
        result.data.dashboardMobilitySustainableSummaryGet ?? null
      )

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Mobility Summary`)
      setMobilitySustainableSummary(null)

      return false
    }
  }

  // get all mobility assistant data
  const getMobilityAssistantData = async () => {
    setMobilityAssistantDataLoading(true)

    await Promise.all([
      getMobilityList<MobilityTransportItem>(
        dashboardMobilityTransportListQuery,
        "dashboardMobilityTransportList",
        setMobilityTransportList,
        "Mobility Transport"
      ),
      getMobilityList<MobilityWeekItem>(
        dashboardMobilityWeeklyListQuery,
        "dashboardMobilityWeeklyList",
        (list) =>
          setMobilityWeeklyList(
            [...list].sort((a, b) => a.weekStart.localeCompare(b.weekStart))
          ),
        "Mobility Weekly"
      ),
      getMobilityList<MobilityCountryItem>(
        dashboardMobilityCountriesListQuery,
        "dashboardMobilityCountriesList",
        setMobilityCountriesList,
        "Mobility Countries"
      ),
      getMobilitySustainableSummary(),
    ])

    setMobilityAssistantDataLoading(false)
  }

  // get all work commute emissions data
  const getCommuteEmissionsData = async () => {
    setCommuteEmissionsDataLoading(true)

    try {
      logger(Status.Api, "[MOBILITY] QUERY commuteEmissionsMonthlyList")
      logger(Status.Api, "[MOBILITY] QUERY commuteModesMonthlyList")

      const [list, modesList] = await Promise.all([
        fetchAllPages<CommuteEmissionsMonthItem>(
          dashboardCommuteEmissionsMonthlyListQuery,
          "dashboardCommuteEmissionsMonthlyList",
          { teamId: team!.id }
        ),
        fetchAllPages<CommuteModeMonthItem>(
          dashboardCommuteModesMonthlyListQuery,
          "dashboardCommuteModesMonthlyList",
          { teamId: team!.id }
        ),
      ])
      logger(Status.Info, "[MOBILITY] commute emissions monthly list", list)
      logger(Status.Info, "[MOBILITY] commute modes monthly list", modesList)

      setCommuteEmissionsMonthlyList(
        [...list].sort((a, b) => a.month.localeCompare(b.month))
      )
      setCommuteModesMonthlyList(modesList)
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Commute Emissions`)
      setCommuteEmissionsMonthlyList([])
      setCommuteModesMonthlyList([])
    }

    setCommuteEmissionsDataLoading(false)
  }

  return (
    <MobilityContext.Provider
      value={{
        mobilityAssistantDataLoading,
        mobilityTransportList,
        mobilityWeeklyList,
        mobilityCountriesList,
        mobilitySustainableSummary,
        getMobilityAssistantData,
        commuteEmissionsDataLoading,
        commuteEmissionsMonthlyList,
        commuteModesMonthlyList,
        getCommuteEmissionsData,
      }}
    >
      {children}
    </MobilityContext.Provider>
  )
}
export { MobilityController, MobilityContext }
